import { findOne, append, createElement, addClass, setAttr, removeClass, on } from 'chirashi'
import anime from 'animejs'

const SONG_LENGTH = 30

export default class TimelineManager {
  constructor(wrapper, goToSongIndex) {
    this.goToSongIndex = goToSongIndex
    this.songTriCurrentIndex = 0
    this.audioProgress = 0

    this.enabled = false
    this.isDragging = false
    this.displacement = 0
    this.oldDisplacement = 0
    this.viewPortDisplacement = 0

    this.viewBoxX = 500
    this.viewBoxY = 100

    this.lineWidth = 4
    this.lineColor = 'rgba(255, 255, 255, 1)'
    this.dotLineColor = 'rgba(255, 255, 255, 0.75)'
    this.dotSpacing = 10

    this.stopDotRadius = 6
    this.lastStopDotRadius = 16
    this.stopDotColor = '#FFFFFF'

    this.dottedCircleRadius = 50
    this.lineLength = this.viewBoxX - this.dottedCircleRadius - this.lastStopDotRadius

    this.stopPointNumber = 4
    this.stopPointDistance = this.lineLength / (this.stopPointNumber - 1)

    // SVG INIT
    this.svg = findOne(wrapper, '.timeline')
    setAttr(this.svg, { viewBox: `0 0 ${this.viewBoxX} ${this.viewBoxY}` })
    this.svgWidth = this.svg.width.baseVal.value

    // DOTTED BASE
    const dottedLine = document.createElementNS('http://www.w3.org/2000/svg', 'line')
    setAttr(dottedLine, {
      x1: this.lastStopDotRadius,
      x2: this.lineLength - this.dottedCircleRadius + this.lastStopDotRadius,
      y1: this.viewBoxY / 2,
      y2: this.viewBoxY / 2,
      stroke: this.dotLineColor,
      'stroke-width': this.lineWidth,
      'stroke-linecap': 'round',
      'stroke-dasharray': `1, ${this.dotSpacing}`
    })

    const dottedCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    setAttr(dottedCircle, {
      cx: this.lastStopDotRadius + 3 * this.stopPointDistance,
      cy: this.viewBoxY / 2,
      r: this.dottedCircleRadius,
      fill: 'rgba(255, 255, 255, 0)',
      stroke: this.dotLineColor,
      'stroke-width': this.lineWidth,
      'stroke-linecap': 'round',
      'stroke-dasharray': `1, ${this.dotSpacing}`
    })

    append(this.svg, dottedLine)
    append(this.svg, dottedCircle)

    for(let i = 0; i < this.stopPointNumber; i += 1) {
      const stopDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
      setAttr(stopDot, {
        cx: this.lastStopDotRadius + i * this.stopPointDistance,
        cy: this.viewBoxY / 2,
        r: i === this.stopPointNumber - 1 ? this.lastStopDotRadius : this.stopDotRadius,
        fill: i === this.stopPointNumber - 1 ? this.dotLineColor : this.stopDotColor,
      })

      append(this.svg, stopDot)
    }

    // FULL PROGRESS
    this.fullLine = document.createElementNS('http://www.w3.org/2000/svg', 'line')
    setAttr(this.fullLine, {
      x1: this.lastStopDotRadius,
      x2: this.lastStopDotRadius,
      y1: this.viewBoxY / 2,
      y2: this.viewBoxY / 2,
      stroke: this.lineColor,
      'stroke-width': this.lineWidth,
    })

    this.progressDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    addClass(this.progressDot, 'timeline__thumb')
    setAttr(this.progressDot, {
      cx: this.lastStopDotRadius,
      cy: this.viewBoxY / 2,
      r: this.lastStopDotRadius,
      fill: this.stopDotColor,
    })

    append(this.svg, this.fullLine)
    append(this.svg, this.progressDot)

    on(this.progressDot, { mousedown: (e) => { this.handleMouseDown(e) } })

    on(window, {
      mousemove: (e) => { this.handleMouseMove(e) },
      mouseup: (e) => { this.handleMouseUp(e) },
    })
  }

  handleMouseDown(e) {
    this.isDragging = true
    this.startClickX = e.clientX
  }

  handleMouseUp() {
    if (this.isDragging) {
      const selectedSongIndex = Math.floor((this.viewPortDisplacement - this.lastStopDotRadius) / this.stopPointDistance)
      this.goToSongIndex(selectedSongIndex)

      let animTarget = this.lastStopDotRadius
      if (selectedSongIndex !== 3) {
        if (selectedSongIndex > this.songTriCurrentIndex) {
          animTarget = this.lastStopDotRadius + selectedSongIndex * this.stopPointDistance
        } else {
          const progress = (this.songTriCurrentIndex * 30 + this.audioProgress) / 90
          animTarget = this.lastStopDotRadius + progress * this.lineLength
        }
      }

      anime.timeline()
        .add({
          targets: this.fullLine,
          x2: animTarget,
          duration: 250,
          easing: 'easeOutExpo',
          offset: 0,
        })
        .add({
          targets: this.progressDot,
          cx: animTarget,
          duration: 250,
          easing: 'easeOutExpo',
          offset: 0,
          complete: () => {
            this.isDragging = false
            this.goToSongIndex(selectedSongIndex)
          }
        })
    }
  }

  handleMouseMove(e) {
    e.preventDefault()

    if (this.isDragging) {
      this.displacement = this.oldDisplacement + e.clientX - this.startClickX
      this.viewPortDisplacement = this.lastStopDotRadius + (this.displacement / this.svgWidth) * 500

      if (this.viewPortDisplacement < this.lastStopDotRadius) {
        this.displacement = 0
        this.viewPortDisplacement = this.lastStopDotRadius
        this.moveThumb(this.lastStopDotRadius)
      } else if (this.viewPortDisplacement > this.lineLength) {
        this.displacement = this.svgWidth
        this.viewPortDisplacement = this.lastStopDotRadius + this.lineLength
        this.moveThumb(this.lastStopDotRadius + this.lineLength)
      } else {
        this.moveThumb(this.viewPortDisplacement)
      }
    }
  }

  moveThumb(posX) {
    setAttr(this.fullLine, { x2: posX })
    setAttr(this.progressDot, { cx: posX })
  }

  update(songTriCurrentIndex, audioProgress) {
    if (this.songTriCurrentIndex !== songTriCurrentIndex) this.songTriCurrentIndex = songTriCurrentIndex
    this.audioProgress = audioProgress

    if (this.enabled && !this.isDragging) {
      const progress = (songTriCurrentIndex * 30 + audioProgress) / 90
      const thumbX = this.lastStopDotRadius + progress * this.lineLength

      this.oldDisplacement = (thumbX / 500) * this.svgWidth

      this.moveThumb(thumbX)
    }
  }
}


// WEBPACK FOOTER //
// ./components/UI/TimelineManager.js